/**
 * <ImpersonationSessionsBlock> — admin-only visibility into every
 * "portal.impersonation" audit event.
 *
 * Rendered inside Settings > Data & Danger, below AuditBackfillBlock.
 * Shows a compact table (actor → client, mode, when).
 */
import { useEffect, useState } from "react";
import { UserCog, Eye, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function ImpersonationSessionsBlock() {
  const { activeWorkspaceId } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/audit/impersonation-sessions`);
      setSessions(data.sessions || []);
    } catch {
      setSessions([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  return (
    <div className="border border-border p-6 space-y-4" data-testid="settings-impersonation-sessions">
      <div className="flex items-center gap-2">
        <UserCog className="w-4 h-4 text-muted-foreground" />
        <div className="label-eyebrow">Recent impersonation sessions</div>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto rounded-none h-7 px-2"
          onClick={load}
          disabled={loading}
          data-testid="impersonation-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Every time an admin views or acts as a client portal, an audit event is written here.
        Use it to prove chain-of-custody during compliance reviews.
      </p>

      {sessions === null ? (
        <div className="text-xs text-muted-foreground py-4">Loading…</div>
      ) : sessions.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4">
          No impersonation sessions recorded yet.
        </div>
      ) : (
        <div className="border border-border/70 overflow-hidden" data-testid="impersonation-sessions-list">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border/70 bg-secondary/40 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            <div className="col-span-3">Actor</div>
            <div className="col-span-4">Client</div>
            <div className="col-span-2">Mode</div>
            <div className="col-span-3 text-right">When</div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {sessions.map((s, i) => (
              <div
                key={s.id || i}
                className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border/40 hover:bg-secondary/30 items-center text-xs"
                data-testid={`impersonation-session-${i}`}
              >
                <div className="col-span-3 font-medium truncate">{s.actor_name || "—"}</div>
                <div className="col-span-4 truncate">{s.target_label || s.target_id}</div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${s.mode === "act" ? "border-amber-500/50 text-amber-700 bg-amber-500/5" : "border-border text-muted-foreground"}`}>
                    {s.mode === "view" && <Eye className="w-3 h-3" />}
                    {s.mode === "act" && <UserCog className="w-3 h-3" />}
                    {s.mode}
                  </span>
                </div>
                <div className="col-span-3 text-right text-muted-foreground">
                  {fmtRelative(s.at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
